"use client";

import { Check, X } from "lucide-react";
import { useEffect, useState, type CSSProperties } from "react";

import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import { CcLandingMock } from "./CcLandingMock";
import type { LandingMockPart, LandingMockState, LandingSubpageCopy } from "./landing-page-copy";

type CcLandingHeroShowcaseProps = {
  mock: LandingSubpageCopy["mock"];
  callouts: LandingSubpageCopy["callouts"];
  toggle: LandingSubpageCopy["hero"]["toggle"];
  meter: LandingSubpageCopy["hero"]["meter"];
};

/**
 * The hero's example page after oriorai.com: the page sits in the middle
 * with labelled callouts around it, and a before/after switch. It opens on
 * "before" and flips to "after" by itself once, so the visitor sees what
 * changes; after that the switch is theirs. Hovering a callout highlights
 * that part of the page.
 */
export function CcLandingHeroShowcase({ mock, callouts, toggle, meter }: CcLandingHeroShowcaseProps) {
  const reduced = useCcPrefersReducedMotion();
  const [picked, setPicked] = useState<LandingMockState | null>(null);
  const [autoFlipped, setAutoFlipped] = useState(false);
  const [focus, setFocus] = useState<LandingMockPart | null>(null);

  useEffect(() => {
    if (reduced) return;
    const timer = window.setTimeout(() => setAutoFlipped(true), 1800);
    return () => window.clearTimeout(timer);
  }, [reduced]);

  // The visitor's choice wins; otherwise "before" until the one automatic flip.
  const state: LandingMockState = picked ?? (reduced || autoFlipped ? "after" : "before");

  const half = Math.ceil(callouts.length / 2);
  const columns = [callouts.slice(0, half), callouts.slice(half)];

  return (
    <div className="cc-lp-showcase" data-state={state}>
      <div className="cc-lp-showcase__toggle" role="group" aria-label={toggle.label}>
        {(["before", "after"] as const).map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={state === option}
            onClick={() => setPicked(option)}
          >
            {toggle[option]}
          </button>
        ))}
      </div>

      <div className="cc-lp-showcase__stage">
        {columns.map((column, side) => (
          <ul key={side} className={`cc-lp-showcase__callouts cc-lp-showcase__callouts--${side ? "right" : "left"}`}>
            {column.map((callout, index) => (
              <li
                key={callout.id}
                className="cc-lp-callout"
                style={{ "--cc-lp-i": side * half + index } as CSSProperties}
                onMouseEnter={() => setFocus(callout.id as LandingMockPart)}
                onMouseLeave={() => setFocus(null)}
              >
                <span className="cc-lp-callout__icon" aria-hidden="true">
                  {state === "after" ? <Check size={12} strokeWidth={3} /> : <X size={12} strokeWidth={3} />}
                </span>
                <span className="cc-lp-callout__text">{state === "after" ? callout.after : callout.before}</span>
              </li>
            ))}
          </ul>
        ))}

        <div className="cc-lp-showcase__page">
          <CcLandingMock copy={mock} state={state} focus={focus} priority />
          <div className="cc-lp-meter" aria-hidden="true">
            <span className="cc-lp-meter__label">
              {meter.label}
              <small>{meter.note}</small>
            </span>
            <span className="cc-lp-meter__track">
              <i />
            </span>
            <b>{state === "after" ? meter.after : meter.before}</b>
          </div>
        </div>
      </div>
    </div>
  );
}
